// src/admin/pages/AdminWeekends.jsx
import React, { useState, useEffect } from "react";
import TabBlockView from "../../components/TabBlockView";
import { toast } from "react-toastify";
import { weekendsService } from "../../../services/api";
import AdminWeekendInfo from "./AdminWeekendInfo";
import AdminWeekendSkills from "./AdminWeekendSkills";
import AdminWeekendSchedule from "./AdminWeekendSchedule";

// ─── Weekend detail panel (inner tabs: Info / Skills / Schedule / Settings) ───
const WeekendPanel = ({ weekend, onUpdated, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: weekend.name,
    slug: weekend.slug,
    status: weekend.status,
  });
  const [saving, setSaving] = useState(false);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await weekendsService.update(weekend.id, editForm);
      toast.success("Weekend оновлено!");
      setEditing(false);
      onUpdated(response.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Помилка при оновленні");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Видалити weekend "${weekend.name}"? Всі пов'язані дані буде видалено.`)) return;
    try {
      await weekendsService.delete(weekend.id);
      toast.success("Weekend видалено!");
      onDeleted(weekend.id);
    } catch (err) {
      toast.error(err.response?.data?.error || "Помилка при видаленні");
    }
  };

  const statusLabel = { draft: "Чернетка", active: "Активний", archived: "Архів" };
  const statusColor = { draft: "#6c757d", active: "#28a745", archived: "#adb5bd" };

  return (
    <div>
      {/* Weekend meta bar */}
      <div style={styles.metaBar}>
        <div style={styles.metaInfo}>
          <span style={{ ...styles.statusBadge, backgroundColor: statusColor[weekend.status] }}>
            {statusLabel[weekend.status]}
          </span>
          <span style={styles.metaSlug}>/{weekend.slug}</span>
        </div>
        <div style={styles.metaActions}>
          <button style={styles.editMetaButton} onClick={() => setEditing((v) => !v)}>
            {editing ? "Скасувати" : "✏️ Редагувати"}
          </button>
          <button style={styles.deleteMetaButton} onClick={handleDelete}>
            🗑️ Видалити
          </button>
        </div>
      </div>

      {/* Inline edit form */}
      {editing && (
        <form onSubmit={handleEditSubmit} style={styles.editForm}>
          <div style={styles.editRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Назва:</label>
              <input style={styles.input} name="name" value={editForm.name}
                onChange={handleEditChange} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Slug (URL):</label>
              <input style={styles.input} name="slug" value={editForm.slug}
                onChange={handleEditChange} placeholder="napr-spring-2026" required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Статус:</label>
              <select style={styles.input} name="status" value={editForm.status} onChange={handleEditChange}>
                <option value="draft">Чернетка</option>
                <option value="active">Активний</option>
                <option value="archived">Архів</option>
              </select>
            </div>
            <div style={styles.editSaveWrap}>
              <button type="submit" style={styles.saveButton} disabled={saving}>
                {saving ? "Збереження..." : "Зберегти"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Inner tab system */}
      <div style={styles.innerTabs}>
        <TabBlockView
          tabs={[
            { name: "Інформація", content: <AdminWeekendInfo weekend={weekend} /> },
            { name: "Навички",    content: <AdminWeekendSkills weekend={weekend} /> },
            { name: "Розклад",    content: <AdminWeekendSchedule weekend={weekend} /> },
          ]}
        />
      </div>
    </div>
  );
};

// ─── Create weekend form ───────────────────────────────────────────────────────
const CreateWeekendTab = ({ onCreate }) => {
  const [form, setForm] = useState({ name: "", slug: "", status: "draft" });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Auto-generate slug from name if slug hasn't been manually edited
    if (name === "name") {
      setForm((prev) => ({
        ...prev,
        name: value,
        // Only auto-fill slug if it's still in sync with the previous auto-value
        slug: prev.slug === toSlug(prev.name) ? toSlug(value) : prev.slug,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toSlug = (str) =>
    str.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      toast.error("Slug може містити лише малі латинські літери, цифри та дефіси");
      return;
    }
    try {
      setSaving(true);
      const response = await weekendsService.create(form);
      toast.success(`Weekend "${form.name}" створено!`);
      setForm({ name: "", slug: "", status: "draft" });
      onCreate(response.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Помилка при створенні");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.createContainer}>
      <h2 style={styles.createTitle}>Створити новий weekend</h2>
      <form onSubmit={handleSubmit} style={styles.createForm}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Назва:</label>
          <input style={styles.input} name="name" value={form.name}
            placeholder="Наприклад: Весняний Weekend 2026"
            onChange={handleChange} required />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Slug (URL-шлях):
            <span style={styles.labelHint}> — тільки латиниця, цифри, дефіси</span>
          </label>
          <div style={styles.slugPreview}>
            <span style={styles.slugBase}>/</span>
            <input style={{ ...styles.input, flex: 1 }} name="slug" value={form.slug}
              placeholder="vesnianyy-weekend-2026"
              onChange={handleChange} required />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Статус:</label>
          <select style={styles.input} name="status" value={form.status} onChange={handleChange}>
            <option value="draft">Чернетка</option>
            <option value="active">Активний</option>
            <option value="archived">Архів</option>
          </select>
        </div>

        <button type="submit" style={styles.createButton} disabled={saving}>
          {saving ? "Створення..." : "+ Створити weekend"}
        </button>
      </form>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const AdminWeekends = () => {
  const [weekends, setWeekends] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchWeekends = async () => {
    try {
      setLoading(true);
      const response = await weekendsService.getAll();
      setWeekends(response.data);
    } catch (err) {
      setError("Не вдалося завантажити weekends");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWeekends(); }, []);

  const handleCreated = (newWeekend) => {
    setWeekends((prev) => [newWeekend, ...prev]);
  };

  const handleUpdated = (updated) => {
    setWeekends((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
  };

  const handleDeleted = (id) => {
    setWeekends((prev) => prev.filter((w) => w.id !== id));
  };

  // Build tabs: one "+ Створити" tab, then one tab per weekend
  const buildTabs = () => {
    const createTab = {
      name: "+ Створити",
      content: <CreateWeekendTab onCreate={handleCreated} />,
    };

    const weekendTabs = weekends.map((w) => ({
      name: w.name,
      content: (
        <WeekendPanel
          key={w.id}
          weekend={w}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      ),
    }));

    return [createTab, ...weekendTabs];
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Weekends</h1>
        <p style={styles.loadingText}>Завантаження...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Weekends</h1>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchWeekends}>Спробувати знову</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Weekends</h1>
      <TabBlockView tabs={buildTabs()} />
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  loadingText: { padding: "2rem", textAlign: "center", color: "#666" },
  errorContainer: {
    display: "flex", flexDirection: "column",
    alignItems: "center", minHeight: "200px", justifyContent: "center",
  },
  errorText: { color: "#dc3545", marginBottom: "1rem", fontSize: "1.1rem" },
  retryButton: {
    padding: "0.8rem 1.5rem", backgroundColor: "#007bff", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer",
    fontFamily: "'UAFSans', sans-serif", fontWeight: 500,
  },

  // Meta bar (shown at top of each weekend panel)
  metaBar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "0.75rem 1rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    marginBottom: "1rem",
    border: "1px solid #e9ecef",
  },
  metaInfo: { display: "flex", alignItems: "center", gap: "1rem" },
  metaSlug: { fontFamily: "monospace", color: "#495057", fontSize: "0.9rem" },
  statusBadge: {
    padding: "0.2rem 0.6rem", borderRadius: "4px",
    color: "white", fontSize: "0.8rem", fontWeight: 600,
  },
  metaActions: { display: "flex", gap: "0.5rem" },
  editMetaButton: {
    padding: "0.4rem 0.9rem", backgroundColor: "#007bff", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.875rem",
    fontFamily: "'UAFSans', sans-serif",
  },
  deleteMetaButton: {
    padding: "0.4rem 0.9rem", backgroundColor: "#dc3545", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.875rem",
    fontFamily: "'UAFSans', sans-serif",
  },

  // Inline edit form
  editForm: {
    padding: "1rem",
    backgroundColor: "#fff3cd",
    borderRadius: "6px",
    marginBottom: "1rem",
    border: "1px solid #ffc107",
  },
  editRow: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "1fr 1fr 1fr auto",
    alignItems: "end",
  },
  editSaveWrap: { display: "flex", alignItems: "flex-end" },
  saveButton: {
    padding: "0.8rem 1.2rem", backgroundColor: "green", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer",
    fontFamily: "'UAFSans', sans-serif", fontWeight: 500, whiteSpace: "nowrap",
  },

  // Inner tabs wrapper
  innerTabs: { marginTop: "0.5rem" },

  // Create form
  createContainer: {
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  createTitle: {
    marginBottom: "1.5rem", fontSize: "1.3rem", color: "var(--secondary-color)",
  },
  createForm: {
    display: "flex", flexDirection: "column", gap: "1.25rem", maxWidth: "500px",
  },
  slugPreview: { display: "flex", alignItems: "center", gap: "0" },
  slugBase: {
    padding: "0.8rem 0.5rem 0.8rem 0.8rem",
    border: "1px solid #ddd", borderRight: "none",
    borderRadius: "4px 0 0 4px",
    backgroundColor: "#e9ecef", color: "#495057",
    fontFamily: "monospace",
  },
  createButton: {
    padding: "0.9rem 1.5rem", backgroundColor: "green", color: "white",
    border: "none", borderRadius: "4px", cursor: "pointer",
    fontFamily: "'UAFSans', sans-serif", fontWeight: 500, fontSize: "1rem",
    alignSelf: "flex-start",
  },

  // Shared form fields
  formGroup: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  label: { fontWeight: 600, fontSize: "0.9rem" },
  labelHint: { fontWeight: 400, color: "#6c757d", fontSize: "0.8rem" },
  input: {
    padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif", outline: "none", fontSize: "1rem",
  },

  // Filler tabs
  fillerContainer: {
    padding: "2rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "2px dashed #dee2e6",
    textAlign: "center",
  },
  fillerLabel: { fontSize: "1rem", color: "#495057", marginBottom: "0.5rem" },
  fillerHint: { fontSize: "0.85rem", color: "#adb5bd", fontFamily: "monospace" },
};

export default AdminWeekends;